import { useState, useEffect, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { fetchRevenueReport, setFilters, clearError } from '../../../store/slices/revenueSlice';
import revenueService from '../../../services/revenueService';
import AirportDropdown from '../../../components/AirportDropdown/AirportDropdown';
import toast from 'react-hot-toast';

// ─── Formatting helpers ───────────────────────────────────────────────────────

const fmtUSD = (n) =>
  new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(Number(n) || 0);

const fmtPct = (rate) => `${(rate * 100).toFixed(1)}%`;

/** Format dates in UTC to match backend created_at filtering (see admin booking list). */
const fmtUtcDate = (iso) => {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    timeZone: 'UTC',
  });
};

/** Convert a Date to a YYYY-MM-DD string without UTC day shifting. */
const toDateInputValue = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

function defaultDateRange() {
  const end = new Date();
  const start = new Date(end.getTime() - 30 * 24 * 60 * 60 * 1000);
  return {
    dateFrom: toDateInputValue(start),
    dateTo: toDateInputValue(end),
  };
}

// ─── Sub-components ───────────────────────────────────────────────────────────

const SummaryCard = ({ label, value, sub, negative, highlight }) => (
  <div className={`rounded-lg p-6 max-sm:p-4 border transition-all ${highlight
      ? 'bg-white border-jet-yellow shadow-md hover:shadow-lg'
      : 'bg-white border-jet-gray-200 shadow-sm hover:shadow-md'
    }`}>
    <p className={`text-xs font-bold uppercase tracking-widest ${highlight ? 'text-jet-yellow' : 'text-jet-gray-600'
      }`}>
      {label}
    </p>
    <p className={`text-3xl max-sm:text-2xl font-bold mt-3 max-sm:break-words ${highlight
        ? 'text-jet-yellow'
        : negative
          ? 'text-jet-danger'
          : 'text-jet-gray-900'
      }`}>
      {value}
    </p>
    {sub && (
      <p className={`text-xs mt-2 font-medium ${highlight ? 'text-jet-yellow' : 'text-jet-gray-600'
        }`}>
        {sub}
      </p>
    )}
  </div>
);

const LineItem = ({ label, value, indent, deduction, bold, separator }) => (
  <tr className={separator ? 'border-t-2 border-jet-gray-300' : ''}>
    <td
      className={`py-3 pr-4 text-sm ${indent ? 'pl-8 text-jet-gray-600' : bold ? 'font-bold text-jet-gray-900' : 'text-jet-gray-700'
        }`}
    >
      {label}
    </td>
    <td
      className={`py-3 text-right text-sm font-semibold ${deduction ? 'text-jet-danger' : bold ? 'text-jet-gray-900 text-base' : 'text-jet-gray-800'
        }`}
    >
      {deduction && value !== fmtUSD(0) ? `−${value}` : value}
    </td>
  </tr>
);

const StatusBadge = ({ status }) => {
  const map = {
    Confirmed: 'bg-jet-success bg-opacity-10 text-jet-success',
    Completed: 'bg-jet-blue-600 bg-opacity-10 text-jet-blue-600',
    Refunded: 'bg-jet-danger bg-opacity-10 text-jet-danger',
    Cancelled: 'bg-jet-gray-200 text-jet-gray-700',
    Pending: 'bg-jet-warning bg-opacity-10 text-jet-warning',
  };
  return (
    <span
      className={`inline-block px-2.5 py-1 rounded-full text-xs font-semibold ${map[status] || 'bg-jet-gray-100 text-jet-gray-600'
        }`}
    >
      {status}
    </span>
  );
};

// ─── Main Page ────────────────────────────────────────────────────────────────

const RevenuePage = () => {
  const dispatch = useDispatch();
  const { report, aircraft, meta, loading, error, filters } = useSelector(
    (state) => state.revenue
  );

  const { dateFrom: defaultFrom, dateTo: defaultTo } = defaultDateRange();

  const [localFilters, setLocalFilters] = useState(() => ({
    dateFrom: filters.dateFrom || defaultFrom,
    dateTo: filters.dateTo || defaultTo,
    aircraftId: filters.aircraftId || '',
    departureAirportId: filters.departureAirportId || '',
    arrivalAirportId: filters.arrivalAirportId || '',
  }));

  const [exportingCSV, setExportingCSV] = useState(false);
  const [exportingPDF, setExportingPDF] = useState(false);
  const [bookingPage, setBookingPage] = useState(1);
  const BOOKINGS_PER_PAGE = 20;

  // Seed Redux filters and load the initial report once on mount.
  useEffect(() => {
    const initial = {
      dateFrom: filters.dateFrom || defaultFrom,
      dateTo: filters.dateTo || defaultTo,
      aircraftId: filters.aircraftId || '',
      departureAirportId: filters.departureAirportId || '',
      arrivalAirportId: filters.arrivalAirportId || '',
    };
    dispatch(setFilters(initial));
    dispatch(fetchRevenueReport(initial));
    // eslint-disable-next-line react-hooks/exhaustive-deps -- mount-only bootstrap
  }, [dispatch]);

  const handleApplyFilters = useCallback(() => {
    if (localFilters.dateFrom && localFilters.dateTo && localFilters.dateFrom > localFilters.dateTo) {
      toast.error('"From" date must be on or before "To" date');
      return;
    }
    setBookingPage(1);
    dispatch(setFilters(localFilters));
    dispatch(fetchRevenueReport(localFilters));
  }, [dispatch, localFilters]);

  const handleResetFilters = useCallback(() => {
    const reset = {
      ...defaultDateRange(),
      aircraftId: '',
      departureAirportId: '',
      arrivalAirportId: '',
    };
    setLocalFilters(reset);
    setBookingPage(1);
    dispatch(setFilters(reset));
    dispatch(fetchRevenueReport(reset));
  }, [dispatch]);

  const handleExportCSV = async () => {
    setExportingCSV(true);
    try {
      const blob = await revenueService.exportCSV(localFilters);
      triggerDownload(blob, `revenue-report-${localFilters.dateFrom}-to-${localFilters.dateTo}.csv`);
      toast.success('CSV exported successfully');
    } catch (err) {
      toast.error('Failed to export CSV');
    } finally {
      setExportingCSV(false);
    }
  };

  const handleExportPDF = async () => {
    setExportingPDF(true);
    try {
      const blob = await revenueService.exportPDF(localFilters);
      triggerDownload(blob, `revenue-report-${localFilters.dateFrom}-to-${localFilters.dateTo}.pdf`);
      toast.success('PDF exported successfully');
    } catch (err) {
      toast.error('Failed to export PDF');
    } finally {
      setExportingPDF(false);
    }
  };

  const allBookings = report?.bookings || [];
  const totalPages = Math.ceil(allBookings.length / BOOKINGS_PER_PAGE);
  const pagedBookings = allBookings.slice(
    (bookingPage - 1) * BOOKINGS_PER_PAGE,
    bookingPage * BOOKINGS_PER_PAGE
  );

  return (
    <div className="min-h-screen bg-jet-gray-50 p-6 max-sm:p-3">
      <div className="max-w-full mx-auto">

        {/* ── Page Header ── */}
        <div className="mb-8 max-sm:mb-6">
          <h1 className="text-3xl max-sm:text-2xl font-bold text-jet-gray-900">Revenue Management</h1>
          <p className="text-sm text-jet-gray-600 mt-2">
            Net payout breakdown — sourced from the booking ledger
          </p>
        </div>

        {/* ── Disclaimer ── */}
        <div className="bg-amber-50 border-l-4 border-amber-400 rounded-r-lg px-5 py-4 mb-6 text-sm text-amber-900">
          <strong>Pre-tax — Net of platform fees.</strong> PJO is responsible for own tax filings. All figures are in USD.
        </div>

        {/* ── Filters ── */}
        <div className="bg-white border border-jet-gray-200 rounded-lg p-5 mb-6 shadow-sm">
          <h2 className="text-sm font-bold text-jet-gray-900 mb-4">Filters</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
            <div>
              <label className="block text-xs font-semibold text-jet-gray-700 mb-2">From (UTC)</label>
              <input
                type="date"
                value={localFilters.dateFrom}
                max={localFilters.dateTo || defaultTo}
                onChange={(e) =>
                  setLocalFilters((f) => ({ ...f, dateFrom: e.target.value }))
                }
                className="w-full px-3 py-2 text-sm border border-jet-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-jet-yellow focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-jet-gray-700 mb-2">To (UTC)</label>
              <input
                type="date"
                value={localFilters.dateTo}
                min={localFilters.dateFrom}
                max={defaultTo}
                onChange={(e) =>
                  setLocalFilters((f) => ({ ...f, dateTo: e.target.value }))
                }
                className="w-full px-3 py-2 text-sm border border-jet-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-jet-yellow focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-jet-gray-700 mb-2">Aircraft</label>
              <select
                value={localFilters.aircraftId}
                onChange={(e) =>
                  setLocalFilters((f) => ({ ...f, aircraftId: e.target.value }))
                }
                className="w-full px-3 py-2 text-sm border border-jet-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-jet-yellow focus:border-transparent"
              >
                <option value="">All Aircraft</option>
                {aircraft.map((a) => (
                  <option key={a.id} value={a.id}>
                    {a.registration_number}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs font-semibold text-jet-gray-700 mb-2">Departure</label>
              <AirportDropdown
                value={localFilters.departureAirportId}
                onChange={(e) =>
                  setLocalFilters((f) => ({ ...f, departureAirportId: e.target.value }))
                }
                placeholder="Select Departure"
                name="departureAirportId"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-jet-gray-700 mb-2">Arrival</label>
              <AirportDropdown
                value={localFilters.arrivalAirportId}
                onChange={(e) =>
                  setLocalFilters((f) => ({ ...f, arrivalAirportId: e.target.value }))
                }
                placeholder="Select Arrival"
                name="arrivalAirportId"
              />
            </div>
          </div>
          <div className="flex gap-3 mt-4">
            <button
              onClick={handleApplyFilters}
              disabled={loading}
              className="px-5 py-2 text-sm font-bold bg-jet-yellow text-jet-primary rounded-lg hover:bg-yellow-400 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {loading ? 'Loading…' : 'Apply Filters'}
            </button>
            <button
              onClick={handleResetFilters}
              disabled={loading}
              className="px-5 py-2 text-sm font-semibold border border-jet-gray-300 rounded-lg bg-white hover:bg-jet-gray-50 disabled:opacity-50 transition-colors"
            >
              Reset
            </button>
          </div>
        </div>

        {/* ── Error State ── */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6 text-sm text-red-700 flex items-center justify-between">
            <span>{error}</span>
            <button
              onClick={() => dispatch(clearError())}
              className="text-red-500 hover:text-red-700 ml-4 font-bold"
            >
              ✕
            </button>
          </div>
        )}

        {/* ── Loading State ── */}
        {loading && (
          <div className="flex items-center justify-center py-24">
            <div className="text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-4 border-jet-gray-200 border-t-jet-yellow mx-auto mb-4" />
              <p className="text-jet-gray-600 text-sm font-medium">Calculating revenue…</p>
            </div>
          </div>
        )}

        {/* ── Report Content ── */}
        {!loading && report && (
          <>
            {/* ── Summary Cards ── */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-8">
              <SummaryCard
                label="Gross Booking Value"
                value={fmtUSD(report.grossBookingValue)}
                sub={`${report.confirmedBookings} confirmed booking${report.confirmedBookings !== 1 ? 's' : ''}`}
              />
              <SummaryCard
                label="JS360 Commission"
                value={fmtUSD(report.js360Commission?.totalCommission)}
                sub="Progressive tier deduction"
                negative
              />
              <SummaryCard
                label="Gateway Fees"
                value={fmtUSD(report.gatewayFees?.totalGatewayFees)}
                sub="Stripe 2.9% / PayPal 3.5%"
                negative
              />
              <SummaryCard
                label="Refunds"
                value={fmtUSD(report.refundsTotal)}
                sub={`${report.refundedBookings} refunded booking${report.refundedBookings !== 1 ? 's' : ''}`}
                negative
              />
              <SummaryCard
                label="Cancellation Fees"
                value={fmtUSD(report.cancellationFeesTotal)}
                sub="Per contract terms"
              />
              <SummaryCard
                label="NET PJO PAYOUT"
                value={fmtUSD(report.netPayout)}
                sub="Pre-tax, net of all fees"
                highlight
              />
            </div>

            {/* ── Itemised Breakdown ── */}
            <div className="bg-white border border-jet-gray-200 rounded-lg p-6 max-sm:p-4 mb-8 shadow-sm">
              <h2 className="text-base font-bold text-jet-gray-900 mb-5">Itemised Breakdown</h2>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <tbody>
                    <LineItem
                      label="Gross Booking Value"
                      value={fmtUSD(report.grossBookingValue)}
                    />
                    <LineItem
                      label={`Less: JS360 Commission (flat bands ≤$${meta?.commissionTiers?.tier1Max?.toLocaleString?.() || '3,000'} / ≤$${meta?.commissionTiers?.tier2Max?.toLocaleString?.() || '6,000'} / ≤$${meta?.commissionTiers?.tier3Max?.toLocaleString?.() || '9,000'})`}
                      value={fmtUSD(report.js360Commission?.totalCommission)}
                      deduction
                    />
                    <LineItem
                      label={`Tier 1 — ${fmtPct(meta?.commissionTiers?.tier1Rate ?? 0.25)} × ${fmtUSD(report.js360Commission?.tier1Amount)}`}
                      value={fmtUSD(report.js360Commission?.tier1Commission)}
                      indent
                    />
                    <LineItem
                      label={`Tier 2 — ${fmtPct(meta?.commissionTiers?.tier2Rate ?? 0.20)} × ${fmtUSD(report.js360Commission?.tier2Amount)}`}
                      value={fmtUSD(report.js360Commission?.tier2Commission)}
                      indent
                    />
                    <LineItem
                      label={`Tier 3 — ${fmtPct(meta?.commissionTiers?.tier3Rate ?? 0.15)} × ${fmtUSD(report.js360Commission?.tier3Amount)}`}
                      value={fmtUSD(report.js360Commission?.tier3Commission)}
                      indent
                    />
                    <LineItem
                      label={`Tier 4 — ${fmtPct(meta?.commissionTiers?.tier4Rate ?? 0.10)} × ${fmtUSD(report.js360Commission?.tier4Amount)}`}
                      value={fmtUSD(report.js360Commission?.tier4Commission)}
                      indent
                    />
                    <LineItem
                      label={`Less: Gateway Fees (Stripe ${fmtPct(meta?.gatewayRates?.stripe ?? 0.029)} / PayPal ${fmtPct(meta?.gatewayRates?.paypal ?? 0.035)})`}
                      value={fmtUSD(report.gatewayFees?.totalGatewayFees)}
                      deduction
                    />
                    <LineItem
                      label={`Stripe ${fmtPct(meta?.gatewayRates?.stripe ?? 0.029)} × ${fmtUSD(report.gatewayFees?.stripeGMV)}`}
                      value={fmtUSD(report.gatewayFees?.stripeFees)}
                      indent
                    />
                    <LineItem
                      label={`PayPal ${fmtPct(meta?.gatewayRates?.paypal ?? 0.035)} × ${fmtUSD(report.gatewayFees?.paypalGMV)}`}
                      value={fmtUSD(report.gatewayFees?.paypalFees)}
                      indent
                    />
                    <LineItem
                      label="Less: Refunds & Deductions"
                      value={fmtUSD(report.refundsTotal)}
                      deduction
                    />
                    <LineItem
                      label="Plus: Cancellation Fees (per contract)"
                      value={fmtUSD(report.cancellationFeesTotal)}
                    />
                    <LineItem
                      label="NET PJO PAYOUT"
                      value={fmtUSD(report.netPayout)}
                      bold
                      separator
                    />
                  </tbody>
                </table>
              </div>
            </div>

            {/* ── Booking Detail Table ── */}
            <div className="bg-white border border-jet-gray-200 rounded-lg overflow-hidden shadow-sm mb-8">
              <div className="px-6 max-sm:px-4 py-4 border-b border-jet-gray-200 flex items-center justify-between max-sm:flex-col max-sm:items-start gap-2 bg-jet-gray-50">
                <h2 className="text-base font-bold text-jet-gray-900">
                  Booking Detail
                  <span className="ml-2 text-sm font-normal text-jet-gray-600">
                    ({allBookings.length} record{allBookings.length !== 1 ? 's' : ''})
                  </span>
                </h2>
                {allBookings.length > 0 && (
                  <p className="text-xs text-jet-gray-500">
                    Page {bookingPage} of {totalPages || 1}
                  </p>
                )}
              </div>

              {allBookings.length === 0 ? (
                <div className="py-16 text-center text-jet-gray-400">
                  <svg className="w-12 h-12 mx-auto mb-3 opacity-30" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  <p className="text-sm">No bookings found for this period.</p>
                </div>
              ) : (
                <>
                  <div className="overflow-x-auto">
                    <table className="min-w-full text-sm">
                      <thead>
                        <tr className="bg-jet-gray-50 border-b border-jet-gray-200">
                          <th className="px-6 py-3 text-left text-xs font-bold text-jet-gray-700 uppercase tracking-wider">Booking Ref</th>
                          <th className="px-6 py-3 text-left text-xs font-bold text-jet-gray-700 uppercase tracking-wider">Flight</th>
                          <th className="px-6 py-3 text-left text-xs font-bold text-jet-gray-700 uppercase tracking-wider">Route</th>
                          <th className="px-6 py-3 text-left text-xs font-bold text-jet-gray-700 uppercase tracking-wider">Date</th>
                          <th className="px-6 py-3 text-right text-xs font-bold text-jet-gray-700 uppercase tracking-wider">Pax</th>
                          <th className="px-6 py-3 text-right text-xs font-bold text-jet-gray-700 uppercase tracking-wider">Gross</th>
                          <th className="px-6 py-3 text-right text-xs font-bold text-jet-gray-700 uppercase tracking-wider">Refunded amount</th>
                          <th className="px-6 py-3 text-right text-xs font-bold text-jet-gray-700 uppercase tracking-wider">Cancellation Fee</th>
                          <th className="px-6 py-3 text-left text-xs font-bold text-jet-gray-700 uppercase tracking-wider">Method</th>
                          <th className="px-6 py-3 text-right text-xs font-bold text-jet-gray-700 uppercase tracking-wider">GW Fee</th>
                          <th className="px-6 py-3 text-left text-xs font-bold text-jet-gray-700 uppercase tracking-wider">Reason</th>
                          <th className="px-6 py-3 text-left text-xs font-bold text-jet-gray-700 uppercase tracking-wider">Status</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-jet-gray-100">
                        {pagedBookings.map((b) => (
                          <tr
                            key={b.id}
                            className={`hover:bg-jet-gray-50 transition-colors ${b.is_refund ? 'bg-red-50' : ''
                              }`}
                          >
                            <td className="px-6 py-3 font-mono text-xs text-jet-gray-700">
                              {b.reference_number}
                            </td>
                            <td className="px-6 py-3 text-jet-gray-700">{b.flight_number || '—'}</td>
                            <td className="px-6 py-3 text-jet-gray-700">{b.route || '—'}</td>
                            <td className="px-6 py-3 text-jet-gray-600 whitespace-nowrap">
                              {fmtUtcDate(b.booking_date)}
                            </td>
                            <td className="px-6 py-3 text-right text-jet-gray-700">{b.passenger_count}</td>
                            <td className="px-6 py-3 text-right font-semibold text-jet-success">
                              {fmtUSD(b.gross_amount)}
                            </td>
                            <td className="px-6 py-3 text-right font-semibold text-jet-danger">
                              {b.refund_amount ? `−${fmtUSD(b.refund_amount)}` : '—'}
                            </td>
                            <td className="px-6 py-3 text-right font-semibold text-jet-warning">
                              {b.cancellation_fee ? fmtUSD(b.cancellation_fee) : '—'}
                            </td>
                            <td className="px-6 py-3 text-jet-gray-600">{b.payment_method}</td>
                            <td className="px-6 py-3 text-right text-jet-gray-600">
                              {b.gateway_fee > 0 ? fmtUSD(b.gateway_fee) : '—'}
                            </td>
                            <td className="px-6 py-3 text-sm text-jet-gray-600 max-w-xs truncate" title={b.cancellation_reason || ''}>
                              {b.cancellation_reason ? (
                                <span className="inline-block px-2.5 py-1 rounded-full text-xs font-medium bg-jet-gray-100 text-jet-gray-700">
                                  {b.cancellation_reason.length > 30 
                                    ? `${b.cancellation_reason.substring(0, 30)}...` 
                                    : b.cancellation_reason}
                                </span>
                              ) : '—'}
                            </td>
                            <td className="px-6 py-3">
                              <StatusBadge status={b.status} />
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  {/* Pagination */}
                  {totalPages > 1 && (
                    <div className="px-6 max-sm:px-4 py-4 border-t border-jet-gray-200 flex items-center justify-between gap-2 bg-jet-gray-50">
                      <button
                        onClick={() => setBookingPage((p) => Math.max(1, p - 1))}
                        disabled={bookingPage === 1}
                        className="px-4 max-sm:px-2 py-2 text-xs font-bold border border-jet-gray-300 rounded-lg disabled:opacity-40 hover:bg-white transition-colors flex-shrink-0"
                      >
                        ← Previous
                      </button>
                      <span className="text-xs text-jet-gray-600 font-medium text-center whitespace-nowrap">
                        Showing {(bookingPage - 1) * BOOKINGS_PER_PAGE + 1}–
                        {Math.min(bookingPage * BOOKINGS_PER_PAGE, allBookings.length)} of{' '}
                        {allBookings.length}
                      </span>
                      <button
                        onClick={() => setBookingPage((p) => Math.min(totalPages, p + 1))}
                        disabled={bookingPage === totalPages}
                        className="px-4 max-sm:px-2 py-2 text-xs font-bold border border-jet-gray-300 rounded-lg disabled:opacity-40 hover:bg-white transition-colors flex-shrink-0"
                      >
                        Next →
                      </button>
                    </div>
                  )}
                </>
              )}
            </div>

            {/* ── Export Buttons ── */}
            <div className="flex gap-3 mb-8 max-sm:flex-wrap">
              <button
                onClick={handleExportCSV}
                disabled={exportingCSV || loading || !report}
                className="flex items-center gap-2 px-5 py-2.5 text-sm font-bold border border-jet-gray-300 rounded-lg bg-white hover:bg-jet-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {exportingCSV ? (
                  <span className="animate-spin h-4 w-4 border-2 border-jet-gray-400 border-t-transparent rounded-full" />
                ) : (
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                )}
                Export CSV
              </button>
              <button
                onClick={handleExportPDF}
                disabled={exportingPDF || loading || !report}
                className="flex items-center gap-2 px-5 py-2.5 text-sm font-bold border border-transparent rounded-lg bg-jet-primary text-jet-yellow hover:bg-jet-gray-800 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {exportingPDF ? (
                  <span className="animate-spin h-4 w-4 border-2 border-jet-yellow border-t-transparent rounded-full" />
                ) : (
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                  </svg>
                )}
                Export PDF
              </button>
            </div>

            {/* ── Commission Info ── */}
            {meta && (
              <div className="bg-jet-gray-50 border border-jet-gray-200 rounded-lg p-5 text-xs text-jet-gray-600">
                <p className="mb-2">
                  <strong className="text-jet-gray-900">Commission structure (flat bands):</strong>{' '}
                  {fmtPct(meta.commissionTiers?.tier1Rate ?? 0.25)} on fare ≤ ${meta.commissionTiers?.tier1Max?.toLocaleString?.() || '3,000'},{' '}
                  {fmtPct(meta.commissionTiers?.tier2Rate ?? 0.20)} on ≤ ${meta.commissionTiers?.tier2Max?.toLocaleString?.() || '6,000'},{' '}
                  {fmtPct(meta.commissionTiers?.tier3Rate ?? 0.15)} on ≤ ${meta.commissionTiers?.tier3Max?.toLocaleString?.() || '9,000'},{' '}
                  {fmtPct(meta.commissionTiers?.tier4Rate ?? 0.10)} above.
                </p>
                <p>
                  <strong className="text-jet-gray-900">Gateway fees:</strong>{' '}
                  Stripe {fmtPct(meta.gatewayRates?.stripe ?? 0.029)}, PayPal {fmtPct(meta.gatewayRates?.paypal ?? 0.035)} (passed through at actuals).
                </p>
              </div>
            )}
          </>
        )}

        {/* ── Empty state ── */}
        {!loading && !report && !error && (
          <div className="py-24 text-center text-jet-gray-400">
            <p className="text-sm">Select a date range and click Apply Filters to load your revenue report.</p>
          </div>
        )}
      </div>
    </div>
  );
};

function triggerDownload(blob, filename) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

export default RevenuePage;
